/**
 * FieldLibraryPage — admin PIM attribute-field library (PIM Wave P1 Task 3,
 * design notes Surface 1). `AdminPageHeader` + `FilterToolbar` (search +
 * per-type chips) + a group select + an archived-toggle chip (the kit
 * `FilterToolbar` only expresses ONE single-select chip group plus a date
 * range — it cannot also express an independent group `<select>` and a
 * second binary toggle, so those two extra controls render inline beside it,
 * same idiom `ProductListPage`/`AuditLogPage` use for filter shapes the kit
 * can't fully express) + the kit `AdminDataTable` (built-in loading/error/
 * empty + keyset "Load more").
 *
 * Row click AND an explicit Edit action both open `FieldFormDrawer` in edit
 * mode (design notes: "keep explicit Edit action for a11y"); "Create field"
 * opens it in create mode. Archive shows a light confirm (BottomSheet); the
 * confirm copy calls out that unarchive exists. Unarchive is immediate — no
 * confirm (brief §5).
 *
 * This whole surface sits behind ONE server permission
 * (`marketplace.attribute.manage` gates every `/admin/catalog/*` route) and
 * the route itself will be `RequirePermissionRoute`-gated in Task 11, so —
 * mirroring `CategoryListPage` (also single-permission) — there is no
 * per-action `useRequirePermission` gating here.
 *
 * PIM Wave P3 Task 4 handoff: arriving here via router
 * `location.state.pimSuggestionPrefill` (set by the admin catalog-
 * suggestions drawer after approving a `kind="field"`/`"option"`
 * suggestion) opens the create drawer pre-seeded with the suggested name +
 * field type. The prefill is captured ONCE into local state (a lazy
 * `useState` initializer reading `location.state` at mount) — not read
 * from `location.state` on every render — because the history entry is
 * replaced (state cleared) right after, so a later refresh/back never
 * re-opens the drawer. It is cleared again when this create session closes
 * so a subsequent manual "Create field" never re-seeds stale data.
 */
import { useEffect, useMemo, useState, type ReactElement, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Archive, ArchiveRestore, FolderCog, Pencil } from "lucide-react";

import { BottomSheet, Button, GPill } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import {
  AdminDataTable,
  AdminPageHeader,
  FilterToolbar,
  type DataTableColumn,
} from "@/features/admin/_shared";
import {
  type AttributeField,
  type AttributeFieldFilters,
  type AttributeFieldGroup,
} from "@/features/admin/attribute-fields/api";
import {
  DEFAULT_ATTRIBUTE_FIELD_FILTERS,
  useAttributeFieldGroups,
  useAttributeFields,
  useArchiveAttributeField,
  useUnarchiveAttributeField,
} from "@/features/admin/attribute-fields/hooks";
import { FIELD_TYPE_META, fieldTypeMeta } from "@/features/admin/attribute-fields/lib/fieldTypeMeta";
import { FieldFormDrawer } from "@/features/admin/attribute-fields/components/FieldFormDrawer";
import { FieldParityBadge } from "@/features/admin/attribute-fields/components/FieldParityBadge";
import { GroupManager } from "@/features/admin/attribute-fields/components/GroupManager";
import { PendingChangesBanner } from "@/features/admin/changesets/components/PendingChangesBanner";
// PIM Wave P5 Task 8 — catalog transfer export/import (marketplace.attribute.manage).
import { TransferControls } from "@/features/admin/transfer/components/TransferControls";

/** Search-input debounce, mirrors `AuditLogPage`'s `AUDIT_TEXT_DEBOUNCE_MS`. */
const FIELD_SEARCH_DEBOUNCE_MS = 350;

const SELECT_CLASS =
  "min-h-[44px] rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--g-primary)]";

const ICON_BUTTON_CLASS =
  "grid h-9 w-9 place-items-center rounded-full text-[var(--g-secondary)] hover:bg-[var(--g-elevated)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]";

/** Stable empty-array reference so `groups` never invalidates a `useMemo` dependency on its own. */
const EMPTY_GROUPS: readonly AttributeFieldGroup[] = [];

/** Shape of `location.state.pimSuggestionPrefill` (PIM Wave P3 Task 4). */
interface SuggestionPrefill {
  name: string;
  field_type: string | null;
}

export function FieldLibraryPage(): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const location = useLocation();
  const navigate = useNavigate();

  const [searchDraft, setSearchDraft] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [fieldTypeFilter, setFieldTypeFilter] = useState<string | null>(null);
  const [groupFilter, setGroupFilter] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const [creating, setCreating] = useState(false);
  const [editingField, setEditingField] = useState<AttributeField | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<AttributeField | null>(null);
  const [managingGroups, setManagingGroups] = useState(false);
  // PIM Wave P3 Task 4 handoff — captured once from the incoming history
  // state (see file header comment), not re-read from `location.state`
  // afterward.
  const [suggestionPrefill, setSuggestionPrefill] = useState<SuggestionPrefill | null>(() => {
    const state = location.state as { pimSuggestionPrefill?: SuggestionPrefill } | null;
    return state?.pimSuggestionPrefill ?? null;
  });

  // Open the create drawer for the captured prefill exactly once at mount,
  // then replace the history entry so a refresh/back never re-triggers it.
  useEffect(() => {
    if (suggestionPrefill) {
      setCreating(true);
      navigate(".", { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce the free-text search into the query filter (audit-page precedent).
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchDraft), FIELD_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchDraft]);

  const filters: AttributeFieldFilters = useMemo(
    () => ({
      ...DEFAULT_ATTRIBUTE_FIELD_FILTERS,
      fieldType: fieldTypeFilter,
      groupId: groupFilter,
      // Default hides archived (is_archived=false). "Show archived" REVEALS
      // them alongside active rows (null = no server-side filter), it does
      // not switch to an archived-only view.
      isArchived: showArchived ? null : false,
      q: debouncedSearch.trim() === "" ? null : debouncedSearch.trim(),
    }),
    [fieldTypeFilter, groupFilter, showArchived, debouncedSearch],
  );

  const { data, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage, refetch } =
    useAttributeFields(filters);
  const { data: groupsData } = useAttributeFieldGroups();
  const groups = groupsData?.groups ?? EMPTY_GROUPS;
  const groupNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const group of groups) map.set(group.id, group.name_ru);
    return map;
  }, [groups]);

  const archiveMutation = useArchiveAttributeField();
  const unarchiveMutation = useUnarchiveAttributeField();

  const rows: AttributeField[] = useMemo(
    () => data?.pages.flatMap((page) => page.fields) ?? [],
    [data],
  );

  const handleUnarchive = (field: AttributeField): void => {
    unarchiveMutation.mutate(field.id, {
      onSuccess: () => toast.success(t("pim.fields.unarchiveSuccess")),
      onError: () => toast.error(t("error.generic")),
    });
  };

  const confirmArchive = (): void => {
    if (!archiveTarget) return;
    archiveMutation.mutate(archiveTarget.id, {
      onSuccess: () => {
        toast.success(t("pim.fields.archiveSuccess"));
        setArchiveTarget(null);
      },
      onError: () => toast.error(t("error.generic")),
    });
  };

  const columns: DataTableColumn[] = [
    { key: "name", label: t("pim.fields.columns.name") },
    { key: "type", label: t("pim.fields.columns.type") },
    { key: "group", label: t("pim.fields.columns.group") },
    { key: "weight", label: t("pim.fields.columns.weight"), align: "right" },
    { key: "parity", label: t("pim.fields.columns.parity") },
    { key: "status", label: t("pim.fields.columns.status") },
    { key: "actions", label: t("pim.fields.columns.actions") },
  ];

  const renderCellContent = (field: AttributeField, key: string): ReactNode => {
    switch (key) {
      case "name":
        return (
          <div className="flex flex-col gap-0.5">
            <span className="font-medium">{field.name_ru}</span>
            <span className="font-mono text-xs text-[var(--g-secondary)]">{field.slug}</span>
          </div>
        );
      case "type": {
        const meta = fieldTypeMeta(field.field_type);
        return <GPill tone="neutral" size="sm">{meta ? t(meta.labelKey) : field.field_type}</GPill>;
      }
      case "group":
        return field.group_id ? (groupNameById.get(field.group_id) ?? "—") : "—";
      case "weight": {
        // `matching_weight` is a Decimal serialized as a string (e.g. "0.00",
        // not always the literal "0") — compare numerically so any
        // zero-valued representation collapses to the dash; a non-numeric
        // value (shouldn't happen, but don't crash the row) falls back to
        // rendering the raw string.
        const numericWeight = Number(field.matching_weight);
        return Number.isFinite(numericWeight) && numericWeight === 0
          ? "—"
          : field.matching_weight;
      }
      case "parity":
        return (
          <FieldParityBadge nameRu={field.name_ru} nameKy={field.name_ky} nameEn={field.name_en} />
        );
      case "status":
        return (
          <GPill tone={field.is_archived ? "neutral" : "success"} size="sm">
            {t(field.is_archived ? "pim.fields.status.archived" : "pim.fields.status.active")}
          </GPill>
        );
      case "actions":
        return (
          <div className="flex items-center gap-1">
            <button
              type="button"
              data-testid={`pim-fields-edit-${field.id}`}
              aria-label={t("pim.fields.actions.edit")}
              onClick={(event) => {
                event.stopPropagation();
                setEditingField(field);
              }}
              className={ICON_BUTTON_CLASS}
            >
              <Pencil size={16} strokeWidth={1.75} aria-hidden />
            </button>
            {field.is_archived ? (
              <button
                type="button"
                data-testid={`pim-fields-unarchive-${field.id}`}
                aria-label={t("pim.fields.actions.unarchive")}
                onClick={(event) => {
                  event.stopPropagation();
                  handleUnarchive(field);
                }}
                className={ICON_BUTTON_CLASS}
              >
                <ArchiveRestore size={16} strokeWidth={1.75} aria-hidden />
              </button>
            ) : (
              <button
                type="button"
                data-testid={`pim-fields-archive-${field.id}`}
                aria-label={t("pim.fields.actions.archive")}
                onClick={(event) => {
                  event.stopPropagation();
                  setArchiveTarget(field);
                }}
                className={ICON_BUTTON_CLASS}
              >
                <Archive size={16} strokeWidth={1.75} aria-hidden />
              </button>
            )}
          </div>
        );
      default:
        return "";
    }
  };

  // Archived rows: dimmed TEXT, not reduced hit area — never dim the actions
  // cell (its buttons must stay fully legible/clickable).
  const renderCell = (field: AttributeField, key: string): ReactNode => {
    const content = renderCellContent(field, key);
    if (field.is_archived && key !== "actions") {
      return <span className="opacity-60">{content}</span>;
    }
    return content;
  };

  const renderMobileCard = (field: AttributeField): ReactElement => {
    const meta = fieldTypeMeta(field.field_type);
    return (
      <div className="flex flex-col gap-2">
        {/* Dimmed when archived — text only, per the desktop rule. The action
            row below stays full-opacity/full-hit-area on both breakpoints. */}
        <div className={["flex flex-col gap-1", field.is_archived ? "opacity-60" : ""].join(" ")}>
          <div className="flex items-center justify-between gap-2">
            <span className="font-medium">{field.name_ru}</span>
            <GPill tone={field.is_archived ? "neutral" : "success"} size="sm">
              {t(field.is_archived ? "pim.fields.status.archived" : "pim.fields.status.active")}
            </GPill>
          </div>
          <span className="font-mono text-xs text-[var(--g-secondary)]">{field.slug}</span>
          <div className="flex items-center gap-2 text-xs text-[var(--g-secondary)]">
            <GPill tone="neutral" size="sm">{meta ? t(meta.labelKey) : field.field_type}</GPill>
            {field.group_id ? <span>{groupNameById.get(field.group_id) ?? "—"}</span> : null}
          </div>
        </div>

        {/* The desktop table exposes Edit/Archive via a dedicated "actions"
            column; `AdminDataTable`'s mobile card has no equivalent slot, so
            this row mirrors those same actions here (otherwise mobile admins
            could never reach Archive/Unarchive). */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            data-testid={`pim-fields-card-edit-${field.id}`}
            aria-label={t("pim.fields.actions.edit")}
            onClick={(event) => {
              event.stopPropagation();
              setEditingField(field);
            }}
            className={ICON_BUTTON_CLASS}
          >
            <Pencil size={16} strokeWidth={1.75} aria-hidden />
          </button>
          {field.is_archived ? (
            <button
              type="button"
              data-testid={`pim-fields-card-unarchive-${field.id}`}
              aria-label={t("pim.fields.actions.unarchive")}
              onClick={(event) => {
                event.stopPropagation();
                handleUnarchive(field);
              }}
              className={ICON_BUTTON_CLASS}
            >
              <ArchiveRestore size={16} strokeWidth={1.75} aria-hidden />
            </button>
          ) : (
            <button
              type="button"
              data-testid={`pim-fields-card-archive-${field.id}`}
              aria-label={t("pim.fields.actions.archive")}
              onClick={(event) => {
                event.stopPropagation();
                setArchiveTarget(field);
              }}
              className={ICON_BUTTON_CLASS}
            >
              <Archive size={16} strokeWidth={1.75} aria-hidden />
            </button>
          )}
        </div>
      </div>
    );
  };

  const isEmpty = !isLoading && !isError && rows.length === 0;

  return (
    <div data-testid="pim-fields-page" className="flex flex-col gap-4">
      <AdminPageHeader
        title={t("pim.fields.title")}
        subtitle={t("pim.fields.subtitle")}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <TransferControls />
            <Button
              variant="primary"
              size="md"
              leading="plus"
              data-testid="pim-fields-create"
              onClick={() => setCreating(true)}
              className="min-h-[44px]"
            >
              {t("pim.fields.create")}
            </Button>
          </div>
        }
      />

      <PendingChangesBanner />

      <FilterToolbar
        search={searchDraft}
        onSearchChange={setSearchDraft}
        searchPlaceholder={t("pim.fields.searchPlaceholder")}
        searchAriaLabel={t("pim.fields.searchAriaLabel")}
        chips={FIELD_TYPE_META.map((meta) => ({ id: meta.value, label: t(meta.labelKey) }))}
        activeChipId={fieldTypeFilter}
        onChipChange={setFieldTypeFilter}
        allChipLabel={t("pim.fields.filter.allTypes")}
        testid="pim-fields-filter"
      />

      <div className="flex flex-wrap items-center gap-2" data-testid="pim-fields-extra-filters">
        <label className="sr-only" htmlFor="pim-fields-filter-group">
          {t("pim.fields.filter.groupLabel")}
        </label>
        <select
          id="pim-fields-filter-group"
          data-testid="pim-fields-filter-group"
          value={groupFilter ?? ""}
          onChange={(event) => setGroupFilter(event.target.value === "" ? null : event.target.value)}
          className={SELECT_CLASS}
        >
          <option value="">{t("pim.fields.filter.allGroups")}</option>
          {groups.map((group) => (
            <option key={group.id} value={group.id}>
              {group.name_ru}
            </option>
          ))}
        </select>

        <button
          type="button"
          data-testid="pim-fields-filter-archived"
          aria-pressed={showArchived}
          onClick={() => setShowArchived((v) => !v)}
          className="min-h-[44px] rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]"
        >
          <GPill tone="primary" active={showArchived}>
            {t("pim.fields.filter.showArchived")}
          </GPill>
        </button>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          data-testid="pim-fields-manage-groups"
          onClick={() => setManagingGroups(true)}
          className="min-h-[44px]"
        >
          <FolderCog size={16} strokeWidth={1.75} aria-hidden className="mr-1.5 inline align-[-2px]" />
          {t("pim.groups.manageButton")}
        </Button>
      </div>

      <AdminDataTable<AttributeField>
        columns={columns}
        rows={rows}
        rowKey={(field) => field.id}
        renderCell={renderCell}
        renderMobileCard={renderMobileCard}
        onRowActivate={(field) => setEditingField(field)}
        rowAriaLabel={(field) => field.name_ru}
        testid="pim-fields"
        isLoading={isLoading}
        isError={isError}
        emptyLabel={t("pim.fields.empty")}
        errorLabel={t("error.loadFailed")}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        onLoadMore={() => void fetchNextPage()}
        loadMoreLabel={t("common.loadMore")}
        loadingMoreLabel={t("common.refreshing")}
      />

      {/* The kit `AdminDataTable`'s built-in error branch is text-only (no
          retry slot) — compose a retry action around it rather than forking
          the shared kit component. */}
      {isError ? (
        <div className="flex justify-center">
          <Button
            variant="ghost"
            size="sm"
            data-testid="pim-fields-retry"
            onClick={() => void refetch()}
            className="min-h-[44px]"
          >
            {t("pim.fields.retry")}
          </Button>
        </div>
      ) : null}

      {isEmpty ? (
        <div className="flex justify-center">
          <Button
            variant="secondary"
            size="sm"
            data-testid="pim-fields-empty-create"
            leading="plus"
            onClick={() => setCreating(true)}
            className="min-h-[44px]"
          >
            {t("pim.fields.emptyCta")}
          </Button>
        </div>
      ) : null}

      <FieldFormDrawer
        open={creating || editingField !== null}
        field={editingField}
        createPrefill={suggestionPrefill ?? undefined}
        onClose={() => {
          setCreating(false);
          setEditingField(null);
          setSuggestionPrefill(null);
        }}
      />

      <BottomSheet
        open={archiveTarget !== null}
        onClose={() => setArchiveTarget(null)}
        title={t("pim.fields.archive.confirmTitle")}
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-[var(--g-secondary)]">
            {t("pim.fields.archive.confirmBody", { name: archiveTarget?.name_ru ?? "" })}
          </p>
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setArchiveTarget(null)}
              disabled={archiveMutation.isPending}
              className="min-h-[44px]"
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              data-testid="pim-fields-archive-confirm"
              loading={archiveMutation.isPending}
              onClick={confirmArchive}
              className="min-h-[44px]"
            >
              {t("pim.fields.archive.confirm")}
            </Button>
          </div>
        </div>
      </BottomSheet>

      {/* Sibling-level BottomSheet (never nested inside FieldFormDrawer's or
          this archive confirm's own BottomSheet children) — GroupManager.tsx
          documents why nested BottomSheets are unsafe here (Framer Motion's
          transform breaks a descendant's `position: fixed`). */}
      <BottomSheet
        open={managingGroups}
        onClose={() => setManagingGroups(false)}
        title={t("pim.groups.title")}
      >
        <GroupManager />
      </BottomSheet>
    </div>
  );
}

export default FieldLibraryPage;
